/**
 * Historique des versions d'une application et confirmation de restauration
 * (§3.5, réutilisé par la fiche d'administration §6.3).
 *
 * Un seul composant sert les deux écrans : une seconde implémentation aurait
 * fini par diverger, et l'historique est précisément ce qui doit se lire de la
 * même façon quel que soit le lecteur.
 *
 * Toutes les versions figurent dans la liste, y compris les tentatives
 * échouées : c'est ce qui explique qu'un numéro manque à l'appel entre deux
 * versions en ligne.
 */

import { hasInitialDescription, type AppVersionDto } from '@portail/shared';
import clsx from 'clsx';
import { Archive, GitCompare, RotateCcw, ScrollText, Sparkles } from 'lucide-react';

import { formatDateTime, formatDuration } from '../lib/format';
import { ConfirmDialog } from './ConfirmDialog';
import { DeploymentStateBadge } from './status';
import { Badge, Button, EmptyState, ErrorBanner, LinkButton, Skeleton } from './ui';

/**
 * Restauration proposée en un clic.
 *
 * Le serveur tranche déjà la question dans `restorable` (version réussie, non
 * archivée, non courante). Le mode administrateur ajoute le repli décrit au
 * §6.3 — « rollback vers n'importe quelle version non archivée » — pour les cas
 * où l'API réserverait `restorable` au propriétaire de l'application.
 */
export function canRestore(version: AppVersionDto, admin = false): boolean {
  if (version.restorable) return true;
  return admin && !version.archived && !version.isCurrent && version.state === 'DONE';
}

/** Numéro qui sera attribué à la prochaine version de l'application. */
export function nextVersionNumber(versions: readonly AppVersionDto[]): number {
  return versions.reduce((max, version) => Math.max(max, version.versionNumber), 0) + 1;
}

/** Étiquette courte d'une version, utilisée dans les sélecteurs et les libellés. */
export function versionLabel(version: AppVersionDto): string {
  const comment = version.versionComment?.trim();
  const suffix = comment ? ` — ${comment}` : '';
  return `v${version.versionNumber} · ${formatDateTime(version.createdAt)}${suffix}`;
}

/** Propriétés de l'historique. */
export interface VersionHistoryProps {
  versions: AppVersionDto[];
  loading: boolean;
  /** Message d'erreur de l'API, affiché tel quel. */
  error: string | null;
  onReload: () => void;
  /** Version mise en avant dans le comparateur. */
  selectedVersionId: string | null;
  onSelect: (versionId: string) => void;
  onRollback: (version: AppVersionDto) => void;
  /** Applique la règle de restauration élargie du §6.3. */
  admin?: boolean;
}

/** Liste chronologique décroissante des versions. */
export function VersionHistory({
  versions,
  loading,
  error,
  onReload,
  selectedVersionId,
  onSelect,
  onRollback,
  admin = false,
}: VersionHistoryProps) {
  if (loading) {
    return (
      <div className="space-y-3 p-4" role="status" aria-label="Chargement des versions">
        {Array.from({ length: 3 }, (_unused, index) => (
          <Skeleton key={index} className="h-16 w-full" />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4">
        <ErrorBanner message={error} onRetry={onReload} />
      </div>
    );
  }

  if (versions.length === 0) {
    return (
      <div className="p-4">
        <EmptyState
          title="Aucune version enregistrée"
          description="L'historique se remplira dès le premier déploiement abouti de cette application."
        />
      </div>
    );
  }

  return (
    <ol className="divide-y divide-slate-200 dark:divide-slate-800">
      {versions.map((version) => {
        const selected = version.id === selectedVersionId;
        const failed = version.state === 'FAILED' || version.state === 'CANCELLED';

        return (
          <li
            key={version.id}
            className={clsx(
              'flex flex-wrap items-start gap-4 p-4',
              selected && 'bg-indigo-50/60 dark:bg-indigo-950/30',
            )}
          >
            <div className="w-14 shrink-0">
              <span
                className={clsx(
                  'text-base font-semibold tabular-nums',
                  failed
                    ? 'text-slate-400 line-through dark:text-slate-600'
                    : 'text-slate-900 dark:text-slate-100',
                )}
              >
                v{version.versionNumber}
              </span>
            </div>

            <div className="min-w-0 flex-1 space-y-1.5">
              <div className="flex flex-wrap items-center gap-2">
                {version.isCurrent ? <Badge tone="green">En ligne</Badge> : null}
                {version.type === 'ROLLBACK' ? (
                  <Badge tone="indigo">
                    Restauration
                    {version.rollbackOfVersionNumber !== null
                      ? ` — restaure v${version.rollbackOfVersionNumber}`
                      : null}
                  </Badge>
                ) : null}
                {version.archived ? <Badge tone="amber">Image archivée</Badge> : null}
                {version.state === 'DONE' ? null : <DeploymentStateBadge state={version.state} />}
              </div>

              <p className="text-sm text-slate-800 dark:text-slate-200">
                {version.versionComment?.trim() ? (
                  version.versionComment
                ) : (
                  <span className="italic text-slate-400 dark:text-slate-600">
                    Sans commentaire de version
                  </span>
                )}
              </p>

              {/* Demande en langage naturel : ce qui a été **demandé**, là où le
                  comparatif montre ce qui en a résulté. La distinction compte quand
                  l'auteur n'a pas lu le code produit.

                  L'intitulé suit la nature de la demande. La v1 d'une application
                  écrite par Claude porte une *description*, pas une modification —
                  annoncer « modification demandée » sur la première version laissait
                  croire à un changement d'un état antérieur qui n'a jamais existé.
                  Et une v1 issue d'un artifact collé ne porte d'instruction que si
                  l'auteur a demandé une correction avant la mise en ligne. */}
              {version.modificationInstruction?.trim() ? (
                <p className="flex items-start gap-1.5 rounded border border-slate-200 bg-slate-50 px-2 py-1 text-xs text-slate-600 dark:border-slate-800 dark:bg-slate-950 dark:text-slate-400">
                  <Sparkles className="mt-0.5 h-3 w-3 shrink-0" aria-hidden="true" />
                  <span>
                    {version.versionNumber > 1
                      ? 'Modification demandée'
                      : hasInitialDescription(version.modificationInstruction)
                        ? 'Application décrite par son auteur'
                        : 'Correction demandée avant la mise en ligne'}{' '}
                    : «&nbsp;{version.modificationInstruction.trim()}&nbsp;»
                  </span>
                </p>
              ) : null}

              <p className="text-xs text-slate-500 dark:text-slate-400">
                {formatDateTime(version.createdAt)} ·{' '}
                {version.author?.displayName ?? 'Auteur inconnu'}
                {version.durationMs !== null ? ` · ${formatDuration(version.durationMs)}` : null}
              </p>

              <p className="break-all font-mono text-xs text-slate-500 dark:text-slate-500">
                {version.imageTag ?? 'aucune image'}
              </p>

              {version.archived ? (
                <p className="flex items-start gap-1.5 text-xs text-amber-800 dark:text-amber-300">
                  <Archive className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                  L'image de cette version a été purgée du registre : elle ne peut plus être
                  restaurée directement. Son code reste consultable et comparable, et peut être
                  redéployé par le pipeline complet via « Mettre à jour ».
                </p>
              ) : null}

              {failed ? (
                <p className="text-xs text-red-700 dark:text-red-400">
                  Cette tentative n'a pas abouti : le numéro v{version.versionNumber} n'a donc
                  jamais été mis en ligne, et manque à la suite des versions actives.
                </p>
              ) : null}
            </div>

            <div className="flex flex-wrap items-center gap-2">
              {/* Les journaux d'un déploiement restent consultables après coup :
                  c'est le seul endroit qui dit *pourquoi* une tentative a
                  échoué. Sans ce lien, l'écran de progression était perdu dès
                  qu'on le quittait. */}
              <LinkButton to={`/deploiements/${version.id}`}>
                <ScrollText className="h-3.5 w-3.5" aria-hidden="true" />
                {failed ? "Voir l'échec" : 'Voir les logs'}
              </LinkButton>
              <Button
                variant={selected ? 'primary' : 'secondary'}
                aria-pressed={selected}
                onClick={() => onSelect(version.id)}
              >
                <GitCompare className="h-3.5 w-3.5" aria-hidden="true" />
                Comparer
              </Button>
              {canRestore(version, admin) ? (
                <Button variant="secondary" onClick={() => onRollback(version)}>
                  <RotateCcw className="h-3.5 w-3.5" aria-hidden="true" />
                  Restaurer cette version
                </Button>
              ) : null}
            </div>
          </li>
        );
      })}
    </ol>
  );
}

/** Propriétés de la confirmation de restauration. */
export interface RollbackConfirmDialogProps {
  /** Version visée ; `null` ferme la boîte de dialogue. */
  version: AppVersionDto | null;
  /** Nom de l'application, rappelé dans le texte. */
  slug: string;
  /** Numéro que portera l'entrée de version créée par la restauration. */
  nextNumber: number;
  busy: boolean;
  error: string | null;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * Confirmation de rollback, identique côté utilisateur et côté administration.
 *
 * Elle énonce les trois faits qui surprennent sinon après coup : la version
 * exacte qui repart en ligne, l'absence de reconstruction, et la création d'une
 * nouvelle entrée dans l'historique — rien n'est réécrit ni effacé.
 */
export function RollbackConfirmDialog({
  version,
  slug,
  nextNumber,
  busy,
  error,
  onConfirm,
  onCancel,
}: RollbackConfirmDialogProps) {
  return (
    <ConfirmDialog
      open={version !== null}
      title={
        version ? `Restaurer la version v${version.versionNumber} ?` : 'Restaurer la version ?'
      }
      description={
        <>
          <p>
            <span className="font-mono">{slug}</span> repassera sur l'image de la version{' '}
            <strong>v{version?.versionNumber}</strong>
            {version?.versionComment?.trim() ? ` (« ${version.versionComment.trim()} »)` : null}.
          </p>
          <ul className="mt-3 list-inside list-disc space-y-1">
            <li>
              Aucune reconstruction : l'image existe déjà dans le registre, la bascule prend moins
              d'une minute.
            </li>
            <li>
              L'historique reste linéaire — une nouvelle entrée <strong>v{nextNumber}</strong> de
              type « restauration » est créée et référence la version v{version?.versionNumber}.
              Rien n'est effacé.
            </li>
            <li>L'adresse publique ne change pas ; l'application répond de nouveau aussitôt.</li>
          </ul>
        </>
      }
      confirmLabel="Restaurer cette version"
      busy={busy}
      error={error}
      onConfirm={onConfirm}
      onCancel={onCancel}
    />
  );
}
